/**
 * Button step — RN counterpart of the web widget's
 * `components/steps/buttons-step.tsx`.
 *
 * One payload, two presentations: `presentation: "free"` renders chat
 * quick-reply chips (wrapping, aligned by the caller — centred in voice mode,
 * right in chat mode), anything else renders the boxed single-choice list with
 * radio affordances and hairline dividers.
 *
 * Web gets its hover dim and `disabled:` opacity from CSS. RN has neither, so
 * both come from <Touchable>'s press/disabled states and no button carries
 * state styling of its own.
 */

import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { Animated, Easing, StyleSheet, Text, View } from "react-native";
import { ExternalLink, LoaderCircle } from "lucide-react-native";
import type { ComponentTheme } from "@ringg/core";

import type { ButtonItem, ButtonsPayload } from "../../lib/component-types";
import { GradientFill, solidColor } from "../../lib/gradient";
import { ringgId } from "../../lib/testing";
import { mergeTheme } from "../../lib/theme-utils";
import { resolveFontFamily, useWidgetTheme } from "../../lib/widget-theme";
import { Touchable } from "../ui/pressable";

interface ButtonsStepProps {
  payload: ButtonsPayload;
  isProcessing?: boolean;
  // Quick-reply chip row alignment: "center" for voice mode, "end" for chat mode.
  align?: "center" | "end";
  onButtonClick: (buttonId: string, action: ButtonItem["action"]) => void;
}

interface SpinnerProps {
  size: number;
  color: string;
}

/** A button surface: `background` may be a gradient, `foreground` never is. */
interface ButtonSurface {
  background: string;
  foreground: string;
}

/** One turn per second — Tailwind's `animate-spin`. */
const SPIN_DURATION_MS = 1000;

const Spinner: FC<SpinnerProps> = ({ size, color }) => {
  const rotation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(Animated.timing(rotation, { toValue: 1, duration: SPIN_DURATION_MS, easing: Easing.linear, useNativeDriver: true }));
    animation.start();
    return () => animation.stop();
  }, [rotation]);

  return (
    <Animated.View style={{ transform: [{ rotate: rotation.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] }) }] }}>
      <LoaderCircle size={size} color={color} />
    </Animated.View>
  );
};

// Quick-reply chips: filled pills, honoring the payload's per-button emphasis
const buttonSurface = (theme: Required<ComponentTheme>, style: ButtonItem["style"]): ButtonSurface => {
  switch (style) {
    case "secondary":
    case "outline":
      return { background: theme.surfaceColor, foreground: theme.textColor };
    case "destructive":
      return { background: theme.errorColor, foreground: theme.primaryTextColor };
    case "primary":
    default:
      return { background: theme.primaryColor, foreground: theme.primaryTextColor };
  }
};

const ButtonsStep: FC<ButtonsStepProps> = ({ payload, isProcessing, align = "end", onButtonClick }) => {
  // Web reads this from the router's ThemeContext; RN resolves the same
  // widget→component mapping locally (see components/interactive-flow.tsx).
  const widgetTheme = useWidgetTheme();
  const theme = useMemo(
    () =>
      mergeTheme({
        primaryColor: widgetTheme.primaryColor,
        primaryTextColor: widgetTheme.primaryTextColor,
        backgroundColor: widgetTheme.backgroundColor,
        surfaceColor: widgetTheme.surfaceColor,
        textColor: widgetTheme.textColor,
        mutedTextColor: widgetTheme.mutedTextColor,
        borderColor: widgetTheme.borderColor,
        ...payload.theme,
      }),
    [payload.theme, widgetTheme],
  );
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const { data } = payload;
  const isFreePresentation = data.presentation === "free";
  const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);

  const handleClick = async (button: ButtonItem) => {
    if (button.action.type === "navigate") {
      onButtonClick(button.id, button.action);
      return;
    }

    setLoadingButtonId(button.id);
    try {
      await onButtonClick(button.id, button.action);
    } finally {
      setLoadingButtonId(null);
    }
  };

  const title = data.title ? (
    <Text testID={ringgId("buttons-step-title")} style={[styles.title, { color: theme.textColor, fontFamily }]}>
      {data.title}
    </Text>
  ) : null;

  if (isFreePresentation) {
    // Web also tags the row with `data-ringg-align`; RN has no attribute
    // namespace beyond testID, and the alignment is readable from the layout.
    return (
      <View testID={ringgId("quick-replies")} style={styles.root}>
        {title}
        <View style={[styles.chipRow, align === "center" ? styles.alignCenter : styles.alignEnd]}>
          {data.buttons.map((button) => {
            const isLoading = loadingButtonId === button.id;
            const isNavigate = button.action.type === "navigate";
            const isDisabled = isLoading || isProcessing;
            const surface = buttonSurface(theme, button.style);

            return (
              <Touchable key={button.id} testID={ringgId("quick-reply-button")} accessibilityLabel={button.label} disabled={isDisabled} onPress={() => void handleClick(button)} style={styles.chip}>
                <GradientFill color={surface.background} style={StyleSheet.absoluteFill} />
                {isLoading ? <Spinner size={12} color={solidColor(surface.foreground)} /> : null}
                <Text style={[styles.chipLabel, { color: surface.foreground, fontFamily }]}>{button.label}</Text>
                {isNavigate ? <ExternalLink size={12} color={solidColor(surface.foreground)} /> : null}
              </Touchable>
            );
          })}
        </View>
      </View>
    );
  }

  // Boxed presentation: a single-choice list with radio affordances and hairline dividers
  return (
    <View>
      {title}
      <View testID={ringgId("options-list")} style={styles.optionsList}>
        {data.buttons.map((button, index) => {
          const isLoading = loadingButtonId === button.id;
          const isNavigate = button.action.type === "navigate";
          const isDisabled = isLoading || isProcessing;
          const isDestructive = button.style === "destructive";
          const isLastRow = index === data.buttons.length - 1;
          const labelColor = isDestructive ? theme.errorColor : theme.textColor;

          return (
            <Touchable
              key={button.id}
              testID={ringgId("option-row")}
              accessibilityLabel={button.label}
              disabled={isDisabled}
              onPress={() => void handleClick(button)}
              // Full-width rows are already past the 44dp target, and slop
              // between stacked rows would make the boundary ambiguous.
              hitSlop={0}
              style={[styles.optionRow, isLastRow ? null : { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: solidColor(theme.borderColor) }]}
            >
              {isLoading ? (
                <Spinner size={16} color={solidColor(labelColor)} />
              ) : (
                <View testID={ringgId("option-radio")} accessibilityElementsHidden importantForAccessibility="no" style={[styles.optionRadio, { borderColor: solidColor(theme.mutedTextColor) }]} />
              )}
              <Text style={[styles.optionLabel, { color: labelColor, fontFamily }]}>{button.label}</Text>
              {isNavigate ? <ExternalLink size={14} color={solidColor(labelColor)} /> : null}
            </Touchable>
          );
        })}
      </View>
    </View>
  );
};

export default ButtonsStep;

const styles = StyleSheet.create({
  root: { width: "100%" },
  title: { fontSize: 14, lineHeight: 20, fontWeight: "500", marginBottom: 8 },
  chipRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  alignCenter: { justifyContent: "center" },
  alignEnd: { justifyContent: "flex-end" },
  // `flexShrink` is 1 by default on web and 0 in RN, so a chip wide enough to
  // fill the row has to be told it may shrink or it overflows the panel.
  chip: { flexDirection: "row", alignItems: "center", gap: 6, height: 36, paddingHorizontal: 16, borderRadius: 9999, flexShrink: 1, overflow: "hidden" },
  chipLabel: { flexShrink: 1, fontSize: 15, fontWeight: "500" },
  optionsList: { flexDirection: "column" },
  optionRow: { flexDirection: "row", alignItems: "center", gap: 12, paddingHorizontal: 8, paddingVertical: 12 },
  optionRadio: { width: 16, height: 16, borderRadius: 9999, borderWidth: 1, flexShrink: 0 },
  optionLabel: { flex: 1, fontSize: 15 },
});
